import { forwardRef, useState } from 'react';
import type {
  ReactNode,
  Ref,
  FocusEvent,
  InputHTMLAttributes,
  AnimationEvent,
  WheelEvent,
} from 'react';
import styled, { css } from 'styled-components';

import { formInputCommon, formStylesCss } from '@yoweb/ui/components/form/formStyles';
import { getSpace } from '@yoweb/ui/styles/utils/theme';
import { isElevatedLabel } from '@yoweb/ui/components/form/formUtils';
import Layout from '@yoweb/ui/components/form/Layout';
import { pxToRem } from '@yoweb/ui/styles/utils';
import type { CurrencySymbols } from '@yoweb/utils/getCurrencySymbol';
import { isDefined } from '@yoweb/utils/isDefined';
import { isString } from '@yoweb/utils/isString';
import { isNumber } from '@yoweb/utils/isNumber';

const checkIfHasValue = (value: InputProps['value']) => {
  if (isString(value, true)) {
    return isString(value);
  } else if (isNumber(value)) {
    return !isNaN(value);
  }

  return false;
};

export type InputProps = {
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  success?: string;
  caption?: JSX.Element | string;
  icon?: JSX.Element;
  disabled?: boolean;
  id: string;
  maxLength?: number;
  /** Hides number spin box (up/down arrows). */
  hideNumberSpinBox?: boolean;
  children?: ReactNode;
  currencySymbol?: CurrencySymbols;
  /** If the language supports it, enforce that an input character is of a certain width. */
  enforceCharacterWidth?: CharacterWidth;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'required'>;

const onAutoFillStart = 'onAutoFillStart';
const onAutoFillCancel = 'onAutoFillCancel';

const Input = (
  { maxLength = 60, children, currencySymbol, ...props }: InputProps,
  ref: Ref<HTMLInputElement>,
) => {
  const currentValue = props.defaultValue ?? props.value;
  const [hasFocus, setFocus] = useState(false);
  const [hasAutoFill, setHasAutofill] = useState(false);
  const hasValue = Boolean(
    hasAutoFill || isDefined(props.value) ? checkIfHasValue(props.value) : currentValue,
  );

  const hasElevatedLabel = isElevatedLabel({
    placeholder: props.placeholder,
    label: props.label,
    hasFocus,
    hasValue,
  });

  const onFocusHandler = (event: FocusEvent<HTMLInputElement>) => {
    setFocus(true);
    props.onFocus?.(event);
  };

  const onBlurHandler = (event: FocusEvent<HTMLInputElement>) => {
    setFocus(false);
    props.onBlur?.(event);
  };

  /**
   * When animation occurs on the input element we fire onAnimationStart event
   * We can detect the css animation name on the event and then determine if an browser
   * autofill has occurred.
   */
  const onAnimationStart = (e: AnimationEvent<HTMLInputElement>) => {
    if (e.animationName === onAutoFillStart) {
      setHasAutofill(true);
    }
  };

  const showCurrencySymbol =
    currencySymbol && hasElevatedLabel && props.type === 'number' ? true : false;

  /**
   * When an input is of type number, the touch pad or wheel
   * increments and decrements the value.  When scrolling with a touch pad
   * you can mistakenly mutate inputs.  For this reason, we are opting out
   * of this default browser behavior.
   */
  const onWheelHandler = (event: WheelEvent<HTMLInputElement>) => {
    if (event.currentTarget.type === 'number') {
      event.currentTarget.blur();
    }

    props.onWheel?.(event);
  };

  return (
    <Layout isFocused={hasFocus} hasValue={hasValue} {...props}>
      {showCurrencySymbol && <StyledCurrencySymbol>{currencySymbol}</StyledCurrencySymbol>}
      <StyledInput
        step={props.type === 'number' ? 'any' : undefined}
        {...props}
        placeholder={props.placeholder}
        elevatedLabel={hasElevatedLabel}
        hasError={!!props.error}
        hasIcon={!!props.icon}
        currencySymbol={showCurrencySymbol}
        maxLength={maxLength}
        onAnimationStart={onAnimationStart}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        onWheel={onWheelHandler}
        ref={ref}
      />
      {children ? children : null}
    </Layout>
  );
};

type StyledInputProps = {
  hasError: boolean;
  hasIcon?: boolean;
  elevatedLabel?: boolean;
  hideNumberSpinBox?: boolean;
  currencySymbol?: boolean;
};

const StyledCurrencySymbol = styled.span`
  position: absolute;
  padding: ${pxToRem(25)} 0 0 ${getSpace('normal1')};
  ${formStylesCss.font};
`;

export const StyledInput = styled.input<StyledInputProps>`
  padding-left: ${getSpace('normal1')};
  padding-right: ${({ hasIcon }) => (hasIcon ? getSpace('medium1') : getSpace('normal1'))};

  /**
  * This autofill hack helps us detect animation name on event onAnimationStart
  */
  /* stylelint-disable-next-line block-no-empty */
  @keyframes onAutoFillStart {
  }

  /* stylelint-disable-next-line block-no-empty */
  @keyframes onAutoFillCancel {
  }

  :-webkit-autofill {
    animation-name: ${onAutoFillStart};
  }

  &:not(:-webkit-autofill) {
    animation-name: ${onAutoFillCancel};
  }

  /* Hides number input spin box */
  ${({ hideNumberSpinBox, type }) =>
    hideNumberSpinBox &&
    type === 'number' &&
    css`
      /* For Firefox */
      &[type='number'] {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -moz-appearance: textfield;
      }

      /* Chrome, Safari, Edge, Opera */
      &[type='number']::-webkit-outer-spin-button,
      &[type='number']::-webkit-inner-spin-button {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-appearance: none;
      }
    `}

  ${formInputCommon};

  ${({ currencySymbol }) =>
    currencySymbol &&
    css`
      padding-left: ${pxToRem(27)};
    `}
`;

export default forwardRef(Input);
